import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AvailableFiltersObject } from '../../../../services/filters';
import Modal from '../../../layouts/Modal/Modal';
import styles from './FiltersPanel.module.scss';
import Button, { ButtonToneProps } from '../../Button/Button';
import Input from '../../Input/Input';
import Select, { Option } from '../../Select/Select';
import ReferenceFilterSelect, { isReferenceFilterType } from '../../ReferenceFilterSelect/ReferenceFilterSelect';
import { DatePicker } from '../../DatePicker/DatePicker';
import { formatFilterKey, getOperatorTranslation } from '../../../../utils/general';
import { FilterState } from '../utils/buildFilterDomain';

type FiltersPanelProps<T extends FilterState> = {
  filters: T;
  setOpenModal: (open: boolean) => void;
  openModalFromOutside: boolean;
  onChange: React.Dispatch<React.SetStateAction<T>>;
  primaryKeys?: { key: string; operator: string }[];
  allAvailableFilters?: AvailableFiltersObject[] | null;
  searchOnly?: boolean;
  filterModelId?: string;
  renderFilters?: (filters: T, set: React.Dispatch<React.SetStateAction<T>>) => React.ReactNode;
};

export default function FiltersPanel<T extends FilterState>({
  filters,
  onChange,
  openModalFromOutside,
  setOpenModal,
  primaryKeys = [],
  allAvailableFilters = [],
  searchOnly,
  filterModelId,
  renderFilters,
}: FiltersPanelProps<T>) {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState<T>({} as T);
  const prominentFilters = primaryKeys || ['id'];

  const numberOfAdditionalFilters = (allAvailableFilters || []).length - prominentFilters.length;

  useEffect(() => {
    if (openModalFromOutside) {
      setDraftFilters(filters);
    }
  }, [openModalFromOutside, filters]);

  const updateOperator = (key: string, operator?: string) => {
    setDraftFilters(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        operator,
      },
    }));
  };

  const updateValue = (key: string, value: any, type?: string) => {
    setDraftFilters(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        type: type ?? prev[key]?.type,
      },
    }));
  };

  const clearAllFilters = () => {
    setDraftFilters({} as T);
  };

  const applyFilters = () => {
    onChange(draftFilters);
    setOpenModal(false);
  };

  return (
    <div className={classNames(styles.root, 'flex flex-wrap gap-16 w-full px-12')}>
      {renderFilters && renderFilters(filters, onChange)}

      {!searchOnly && (
        <Button
          onClick={() => setOpenModal(true)}
          title={t('general.additional_filters')}
          tone={ButtonToneProps.SECONDARY}
          icon="tune"
          numberIndicator={numberOfAdditionalFilters}
        />
      )}

      {openModalFromOutside && (
        <Modal isOpen onClose={() => setOpenModal(false)}>
          <div className={styles.modal}>
            <div className={styles.head}>{t('general.additional_filters')}</div>

            <div className={classNames(styles.content, 'flex flex-col gap-24')}>
              {allAvailableFilters &&
                allAvailableFilters.map(filterDef => {
                  const key = Object.keys(filterDef)[0];
                  const def = filterDef[key];
                  if (!def || !def.operators) return null;

                  const filter = draftFilters[key] || {};
                  const isDisabled = !filter.operator;

                  const operatorOptions: Option[] = def.operators.map(op => ({
                    value: op,
                    label: getOperatorTranslation(op),
                  }));

                  return (
                    <div key={key} className={classNames(styles.filterLine, 'flex gap-4 items-center')}>
                      <Select
                        label={t(`general.${key}`)}
                        options={operatorOptions}
                        defaultValue={operatorOptions.find(o => o.value === filter.operator)}
                        onChange={opt => updateOperator(key, opt?.value)}
                        isClearable
                        menuFixed
                      />

                      {(def.type === 'char' || def.type === 'integer' || def.type === 'float') && (
                        <Input
                          disabled={isDisabled}
                          placeholder={t('general.input_placeholder')}
                          value={filter.value ?? ''}
                          type={def.type === 'char' ? 'text' : 'number'}
                          onChange={e => updateValue(key, e.target.value, def.type)}
                          clearable
                        />
                      )}

                      {def.type === 'selection' && (
                        <Select
                          disabled={isDisabled}
                          options={(def.options || []).map(o => ({
                            value: o,
                            label: formatFilterKey(o, filterModelId),
                          }))}
                          defaultValue={(def.options || [])
                            .map(o => ({ value: o, label: formatFilterKey(o, filterModelId) }))
                            .find(o => o.value === filter.value)}
                          onChange={opt => updateValue(key, opt?.value)}
                          isClearable
                          menuFixed
                        />
                      )}

                      {def.type === 'boolean' && (
                        <Select
                          disabled={isDisabled}
                          options={[
                            { value: 'True', label: t('general.true') },
                            { value: 'False', label: t('general.false') },
                          ]}
                          defaultValue={
                            filter.value
                              ? { value: filter.value, label: t(`general.${filter.value.toLowerCase()}`) }
                              : undefined
                          }
                          onChange={opt => updateValue(key, opt?.value)}
                          isClearable
                          menuFixed
                        />
                      )}

                      {def.type === 'datetime' && (
                        <DatePicker
                          disabled={isDisabled}
                          label={t(`general.${key}`)}
                          defaultValue={filter.value ? dayjs(filter.value) : null}
                          onChange={v => updateValue(key, v, 'datetime')}
                        />
                      )}

                      {isReferenceFilterType(def.type) && (
                        <ReferenceFilterSelect
                          type={def.type}
                          disabled={isDisabled}
                          value={filter.value}
                          onChange={value => updateValue(key, value, def.type)}
                        />
                      )}
                    </div>
                  );
                })}
            </div>

            <div className="flex justify-between px-28 pt-32">
              <Button title={t('general.reset_filter')} tone={ButtonToneProps.SECONDARY} onClick={clearAllFilters} />

              <Button title={t('general.select_filter')} tone={ButtonToneProps.PRIMARY_BLUE} onClick={applyFilters} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
